<template>
  <nav>
    <v-dialog
      v-if="!loadingData"
      v-model="dialog"
      data-testid="activity-edit-dialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      persistent
    >
      <v-card data-testid="activity-edit-card">
        <v-form
          ref="form"
          v-model="valid"
          @submit.prevent
        >
          <v-toolbar
            dark
            color="accent"
          >
            <v-btn
              data-testid="close-button"
              icon
              dark
              @click="onDialogClose"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title class="text-h5 font-weight-bold">
              {{ $t('editActivityTitle') }}
            </v-toolbar-title>
            <v-spacer />
            <v-btn
              data-testid="submit-button"
              type="submit"
              :disabled="loading"
              color="white"
              class="text-body-1 darktext--text text-none rounded-xxl px-4"
              @click="updateActivity"
            >
              {{ $t('save') }}
            </v-btn>
          </v-toolbar>
          <AlertMessages
            v-if="alertValue"
            :alert-type="alertType" 
            :alert-msg="alertMsg"
            @reset="alertValue = false"
          >
          </AlertMessages>
          <v-card-text class="pt-0 pl-6">
            <div class="py-15 px-md-12">
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelTitle') }}
                  </p>
                  <v-text-field
                    v-model="name"
                    data-testid="activity-title-input"
                    solo
                    hide-details="auto"
                    elevation="6"
                    class="rounded-xxl text-body-2 mb-5"
                    :rules="activityTitleRules"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                >
                  <p class="font-weight-bold text-h6 darktext--text">
                    {{ $t('labelCoverImage') }}
                  </p>
                  <v-hover>
                    <template v-slot:default="{ hover }">
                      <v-card
                        align="center"
                        data-testid="activity-cover-card"
                        rounded="md"
                        class="pa-0 rounded-xxl"
                        :elevation="hover ? 8 : 5"
                        max-width="600"
                      >
                        <v-card-actions class="pa-0 ">
                          <v-img
                            class="rounded-xxl"
                            max-height="280"
                            :src="cover != null ? cover : coverURL"
                          />
                          <v-file-input 
                            ref="file_input_cover"
                            v-model="coverImageData"
                            accept="image/png, image/jpeg"
                            data-testid="cover-file-input"
                            class="d-none"
                            @change="uploadCover"
                          />
                          <div class="center-button-on-image">
                            <v-btn
                              :loading="loadingCover"
                              data-testid="upload-cover-button"
                              mx-auto
                              color="accent"
                              class="rounded-xxl text-none px-10"
                              elevation="2"
                              relative
                              bottom
                              left
                              @click="showUploadCover"
                            >
                              {{ $t('upload') }}
                            </v-btn>
                            <v-btn 
                              data-testid="upload-unsplash-cover-button"
                              mx-auto color="accent"
                              class="rounded-xxl text-none px-10 d-block mx-auto mt-3"
                              :class="{'disable-events': loadingCover}"
                              elevation="2"
                              relative
                              bottom
                              left
                              @click="toggleUnsplash">
                            {{ $t('browse') }}
                            </v-btn>
                          </div>
                        </v-card-actions>
                      </v-card>
                    </template>
                  </v-hover>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  class="radio-align-start"
                >
                  <p
                    data-testid="sharing-setting-label"
                    class="font-weight-bold text-h6 darktext--text mt-5"
                  >
                    {{ $t('SharingSettingsLabel') }}
                  </p>
                  <v-radio-group v-model="sharingSettings">
                    <v-radio
                      v-for="option in sharingOptions"
                      :key="option.id"
                      :label="$t('activitySharingSettingsOption'+option.id)"
                      :value="option.id"
                      color="accent"
                      class="black--text option"
                      data-testid="sharing-setting-options"
                      @change="changeSharingOption(option.id)"
                      :disabled="productPlan.attributes && productPlan.attributes.name == 'Free' && option.id != 1"
                    >
                      <template v-slot:label>
                        <div class="option-slot-pro">
                          {{ $t('activitySharingSettingsOption'+option.id) }}
                          <v-chip v-if="productPlan.attributes && productPlan.attributes.name == 'Free' && option.id != 1" class="rounded-lg ml-2 px-2" x-small color="accent" text-color="white">PRO</v-chip>
                        </div>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  class="checkbox-align-start"
                >
                  <p class="font-weight-bold text-h6 darktext--text mt-5">
                    {{ $t('forumSettingsLabel') }}
                  </p>
                  <v-checkbox
                    v-model="checkboxForum"
                    :label="$t('checkboxForumLabel')"
                    color="accent"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  class="checkbox-align-start"
                >
                  <p class="font-weight-bold text-h6 darktext--text mt-5">
                    {{ $t('tagsLabel') }}
                  </p>
                  <v-autocomplete
                    v-model="tags"
                    :items="tagSuggestions"
                    multiple
                    chips
                    clearable
                    outlined
                    small-chips
                    solo
                    hide-selected
                    rounded
                    no-filter
                    return-object
                    data-testid="tags-input"
                    :search-input.sync="search"
                    :menu-props="{ contentClass: 'no-checkboxes' }"
                    @change="onTagSelection"
                    @blur="addNewTag"
                    @keydown.enter.prevent="addNewTag"
                  >
                    <template v-slot:selection="{ item }">
                      <v-chip
                        close
                        @click:close="removeTag(item)"
                        color="accent"
                      >
                        {{ item }}
                      </v-chip>
                    </template>
                    <template v-slot:prepend-inner>
                      <v-progress-circular
                        v-if="isLoading"
                        size="20"
                        indeterminate
                      />
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
              <v-row>
                <v-col
                  cols="12"
                  md="6"
                  class="checkbox-align-start"
                >
                  <p class="font-weight-bold text-h6 darktext--text mt-5">
                    {{ $t('certificateSettingsLabel') }}
                  </p>
                  <v-checkbox
                    v-model="checkboxCertificate"
                    :label="$t('checkboxCertificateLabel')"
                    color="accent"
                  />
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-form>
      </v-card>
    </v-dialog>
    <ConfirmationDialog 
      v-if="dialogVisibleChangeSharingSetting"
      ref="confirmationDialogChangeSharingSetting"
      :visible="dialogVisibleChangeSharingSetting" 
      :cd-title="confirmationDialogTitle" 
      :cd-text="confirmationDialogText" 
      :cd-submit-btn="confirmationDialogSubmitBtn" 
      :cd-cancel-btn="confirmationDialogCancelBtn" 
      data-testid="confirmation-dialog-change-sharing-setting" 
      cd-submit-btn-color="accent"
      @close="resetSharingSetting"
      @proceedSubmit="dialogVisibleChangeSharingSetting=false"
    />
    <UnsplashSearch 
      v-if="showUnsplash"
      ref="unsplashDialog"
      :visible="showUnsplash"
      data-testid="unsplash-dialog"
      @close="showUnsplash = false"
      @applyPhoto="embedUnsplash"
    />
  </nav>
</template>
<script>
import i18n from '../../plugins/i18n'
import { uploadCoverImage, updateActivity,getSharingSetings, getActivityPages, getTags} from '../../api/activityApi'
import { ruleRequired, ruleValidMin2Max70Characters } from '../../rules/formRules.js';
import {eventBus} from '../../eventbus'
import AlertMessages  from '../../components/AlertMessages'
import ConfirmationDialog from '../../components/ConfirmationDialog.vue'
import UnsplashSearch from '@/components/unsplash/UnsplashSearch.vue';
import _ from "lodash";

export default {
    components: {
        AlertMessages,
        ConfirmationDialog,
        UnsplashSearch
    },
    props: {
        visible: Boolean,
        activityId: {
          type: String,
          default: ''
        },
        activityTitle: {
          type: String,
          default: ''
        },
        activityImageURL: {
          type: String,
          default: ''
        },
        activityImagePath: {
          type: String,
          default: ''
        },
        activitySharingSettings: {
          type: String,
          default: ''
        },
        activityCheckboxForum: Boolean,
        activityCheckboxCertificate: {
          type: Boolean,
          default: false
        },
        changeActivityMenu: {
          type: Boolean,
          default: false
        },
        activityTags: {
          type: Array,
          default: () => [],
        }
    },
    data(){
        return{
            valid: true,
            alertMsg: '',
            alertType:'',
            alertValue:false,
            spaceId: window.$cookies.get('space_id'),
            activityTitleRules: ruleRequired('labelTitle').concat(ruleValidMin2Max70Characters('labelTitle')),
            val: '',
            valCover: '',
            valCoverPath: '',
            valSharingSettings:'',
            valCheckboxForum: true,
            valCheckboxCertificate: false,
            coverImageData: null,
            coverURL: require('../../assets/cover-activity.jpg'),
            loadingCover: false,
            buttonDisabled: false,
            sharingOptions :[],
            dialogVisibleChangeSharingSetting: false,
            confirmationDialogTitle: i18n.t('confirmationDialogTitleChangeActivitySharingSetting'),
            confirmationDialogText:  i18n.t('confirmationDialogTextChangeActivitySharingSetting'),
            confirmationDialogCancelBtn: i18n.t('cancel'),
            confirmationDialogSubmitBtn: i18n.t('ok'),
            showUnsplash: false,
            valTags: [],
            tagSuggestions: [],
            isLoading: false,
            search: '',
            debounceTimeout: null,
            abortController: null,
         }
    },
    computed: {
      dialog: {
      get () {
        return this.visible
      },
      set (value) {
        if (!value) {
          this.$emit('close')
        }
         
      }
    },
        name:
        {
            get()
            {
                return this.val
            },
            set(activityTitle)
            {
                this.val = activityTitle
            }
        },
        cover:
        {
            get()
            {
                return this.valCover
            },
            set(activityImageURL)
            {
                this.valCover = activityImageURL
            }
        },
        coverPath:
        {
            get()
            {
                return this.valCoverPath
            },
            set(activityImagePath)
            {
                this.valCoverPath = activityImagePath
            }
        },
        sharingSettings:
        {
            get()
            {
                return this.valSharingSettings
            },
            set(activitySharingSettings)
            {
                this.valSharingSettings = activitySharingSettings
            }
        },
        checkboxForum:{
            get()
            {
                return this.valCheckboxForum
            },
            set(activityCheckboxForum)
            {
                this.valCheckboxForum = activityCheckboxForum
            }
        },
        checkboxCertificate:{
            get()
            {
                return this.valCheckboxCertificate
            },
            set(activityCheckboxCertificate)
            {
                this.valCheckboxCertificate = activityCheckboxCertificate
            }
        },
        tags:{
          get()
          {
              return this.valTags
          },
          set(activityTags)
          {
              this.valTags = activityTags
          }
        },
        loading(){
            if(this.loadingCover || this.buttonDisabled){
                return true
            }
            return false
        },
        productPlan() {
          return this.$store.state.productPlan;
        },
    },
    watch: {
        activityTitle(newVal) {
            this.val = newVal
        },
        activityImageURL(newValCover){
            this.valCover = newValCover
        },
        activityImagePath(newValCoverPath){
            this.valCoverPath = newValCoverPath
        },
        activitySharingSettings(newValsharing) {
            this.valSharingSettings = newValsharing
        },
        activityCheckboxForum(newValCheckboxForum) {
            this.valCheckboxForum = newValCheckboxForum
        },
        activityCheckboxCertificate(newValCertificate) {
            this.valCheckboxCertificate = newValCertificate
        },
        activityTags(tags) {
          this.valTags = tags
        },
        async search(val) {
          if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
          }
          this.debounceTimeout = setTimeout(() => {
            this.fetchTags(val);
          }, 20);
        },
    },
    created() {
        this.loadingData = true
        this.fetchSharingSettings()
        this.name = this.activityTitle
        this.cover = this.activityImageURL
        this.coverPath = this.activityImagePath
        this.sharingSettings = this.activitySharingSettings
        this.checkboxForum = this.activityCheckboxForum
        this.checkboxCertificate = this.activityCheckboxCertificate
        this.alertMsg = ''
        this.loadingData = false
        this.tags = this.activityTags
        this.tagSuggestions = this.activityTags;
    },
    methods: {
        async fetchSharingSettings() {
            try {
                const response = await getSharingSetings()
                if(response){
                    if(response.status == 200) {
                        this.sharingOptions = response.data.data
                    }
                }            
            } catch (e) {
                if(e.response){
                    this.alertValue = true
                    this.alertType = 'error';
                    this.alertMsg =  i18n.t('serverError');
                } 
            }
        },
        onTagSelection() {
          this.search = '';
        },
        addNewTag() {
          const tagExists = this.tagSuggestions.includes(this.search);
          if (this.search && !tagExists && !this.tags.includes(this.search)) {
            this.tags.push(this.search);
            this.tagSuggestions.push(this.search);
          }
          this.search = '';
        },
        removeTag(tag) {
          this.tags = this.tags.filter((t) => t !== tag);
        },

        fetchTags: _.debounce(async function (val) {

          if (!val || val.trim().length < 2) {
            this.tagSuggestions = Array.from(new Set([...this.activityTags, ...this.tags]));
            return;
          }

          this.isLoading = true;

          try {
            const response = await getTags(this.spaceId, val);
            if (response?.status === 200) {
              const entries = response.data.data.map((tag) => tag.attributes.name);
              this.tagSuggestions = Array.from(
                new Set([...entries, ...this.activityTags, ...this.tags])
              );
            }
          } catch (e) {
              if (e.response) {
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("serverError");
              }
          } finally {
            this.isLoading = false;
          }
        }, 500),
        async updateActivity(){
            this.$refs.form.validate()
            if(this.valid){
                this.buttonDisabled = true
                var alertType = ''
                var alertMsg = ''
                var attributes = {}

                if(this.coverPath == null){
                    attributes = {
                        title: this.name.trim(),
                        forum_enabled: this.checkboxForum,
                        certificate_enabled: this.checkboxCertificate,
                        tags: this.tags
                    }
                }else{
                    attributes= {
                        title: this.name.trim(),
                        forum_enabled: this.checkboxForum,
                        certificate_enabled: this.checkboxCertificate,
                        image: this.coverPath,
                        tags: this.tags
                    }
                }

                try {
                    let data = {
                        data: {
                            type: "activities",
                            id: this.activityId, 
                            attributes: attributes,
                            relationships: {
                                space: {
                                    data: {
                                        type: "spaces",
                                        id: this.spaceId
                                    }
                                },
                                "sharing-setting": {
                                    data: {
                                        type: "sharing-settings",
                                        id: this.sharingSettings
                                    }
                                }
                            }
                        }
                    };

                    var response = await updateActivity(this.activityId, data);

                    if(response.status == 200) {
                        alertMsg =  i18n.t('editActivitySuccess')
                        alertType = 'success'

                        this.$emit('proceedSubmit', alertMsg, alertType, this.name, this.cover, this.coverPath, this.sharingSettings,this.checkboxForum, this.checkboxCertificate, this.tags)

                        if(this.sharingSettings != this.activitySharingSettings && (this.sharingSettings == 3 || this.activitySharingSettings == 3) && this.changeActivityMenu){
                            const response_pages = await getActivityPages(this.activityId)
                            eventBus.$emit('changeNavigationInActivity', response_pages.data.data)   
                        }
                        this.dialog = false                       
                    }
                } catch (e) {
                    if(e.response){
                        if (e.response.status == 403 ){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].detail == 'This action is unauthorized.'){
                                    this.$router.push({name: 'PageAccessDenied'})
                                }
                                else {
                                    alertType = 'error'
                                    alertMsg =  i18n.t('serverError')
                                    this.$emit('proceedSubmit', alertMsg, alertType)
                                    this.$emit('close') 
                                }
                            }
                        } else{
                            alertType = 'error'
                            alertMsg =  i18n.t('serverError')
                            this.$emit('proceedSubmit', alertMsg, alertType)
                            this.$emit('close')  
                        }           
                    }
                }
                this.buttonDisabled = false
            }
        },
        onDialogClose(){
          this.$emit('close')
        },
        async uploadCover(){
            if(this.coverImageData.size > 5*1024*1024){
                this.alertType = 'error'
                this.alertMsg =  i18n.t('uploadCoverError')
                this.alertValue = true
            }else {
                this.loadingCover = true
                try {
                    let data = new FormData();
                    data.append('file', this.coverImageData);
                    data.append('type', 'cover');
                    data.append('space_id', this.spaceId);

                    const response = await uploadCoverImage(data) 
                    if(response.status == 200) {
                        this.loadingCover = false
                        this.alertMsg = ''
                        this.cover = URL.createObjectURL(this.coverImageData)
                        this.coverPath = response.data.meta.detail
                    }
                } catch (e) {
                    if(e.response){
                        if (e.response.status == 422 ){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].meta[0].code == 'extensionUnsupported' || e.response.data.errors[i].meta[0].code == 'fileOversize'){
                                    this.alertValue = true
                                    this.alertType = 'error'
                                    this.alertMsg =  i18n.t('uploadCoverError')
                                }else {
                                    this.alertValue = true
                                    this.alertType = 'error'
                                    this.alertMsg = i18n.t('serverError')
                                }
                            }
                        } else if (e.response.status == 403){
                            for(let i = 0; i < e.response.data.errors.length; i++){
                                if(e.response.data.errors[i].detail == 'Storage is full'){
                                    this.alertValue = true
                                    this.alertType = 'error'
                                    this.alertMsg =  i18n.t('storageFullError')
                                }
                                else {
                                    this.$router.push({name: 'PageAccessDenied'})
                                }
                            }
                        }else {
                            this.alertType = 'error'
                            this.alertValue = true
                            this.alertMsg =  i18n.t('serverError')       
                        }                     
                    }
                    this.loadingCover = false
                }
            }
        },
        showUploadCover(){
            this.$refs.file_input_cover.$refs.input.click()
        },
        changeSharingOption(newVal){
          if(newVal == 3 && this.activitySharingSettings != 3)
          {
              this.dialogVisibleChangeSharingSetting = true
          }
        },
        resetSharingSetting(){
            this.sharingSettings = this.activitySharingSettings
            this.dialogVisibleChangeSharingSetting = false
        },

        toggleUnsplash(){
          this.showUnsplash = !this.showUnsplash
        },

        embedUnsplash(photo){
          this.showUnsplash = false
          this.cover = photo.urls.regular
          this.coverPath = photo.urls.regular
        }
    }
}
</script>
<style>
i.v-icon.notranslate.mdi.mdi-radiobox-blank.theme--light {
    color: #7C8386 !important;
}
label.v-label.theme--light {
    color: #263238;
}
/*.v-input--selection-controls__input {
    justify-content: center;
    align-items: center;
}*/
</style>